export type AudioContextLike = {
    readonly state: string;
    resume(): Promise<void> | void;
};

// Subset of the Howler global used here. `state` isn't in Howler's typings,
// but Howler 2.2 tracks its own view of the context lifecycle in it and
// defers `play()` (until its own resume settles) whenever it isn't `running`.
export type HowlerLike = {
    ctx: AudioContextLike | null;
    autoSuspend: boolean;
    state?: string;
};

export type HowlLike = {
    on(event: 'unlock' | 'play', callback: () => void): unknown;
};

export type DocumentLike = Pick<
    Document,
    'visibilityState' | 'addEventListener' | 'removeEventListener'
>;

export type AudioLifecycleOptions = {
    howler: HowlerLike;
    document?: DocumentLike;
    isSuspended: () => boolean;
};

export type AudioLifecycle = {
    isPlaybackReady: () => boolean;
    recover: (trigger?: 'gesture' | 'programmatic') => void;
    track: (howl: HowlLike) => void;
    acquire: () => () => void;
};

const GESTURE_EVENTS = ['pointerdown', 'touchend', 'keydown'] as const;
const LISTENER_OPTIONS: AddEventListenerOptions = {
    capture: true,
    passive: true,
};

export const createAudioLifecycle = ({
    howler,
    document: doc,
    isSuspended,
}: AudioLifecycleOptions): AudioLifecycle => {
    // Howler's idle auto-suspend would otherwise suspend the context after 30
    // seconds without playback, even in a visible tab. Must be disabled before
    // the first Howl creates the context and starts the suspend timer.
    howler.autoSuspend = false;

    // Contexts Howler has unlocked (or played through). Keyed by context so a
    // replacement context doesn't inherit a stale unlock.
    const unlockedContexts = new WeakSet<AudioContextLike>();
    let pendingResume: AudioContextLike | null = null;
    let consumers = 0;

    const isVisible = () => !doc || doc.visibilityState === 'visible';

    // Howler defers playback (its load queue, or waiting on its own resume)
    // whenever the asset or context isn't ready, and plays everything that
    // was deferred at once when it becomes ready. Only let calls through when
    // both the native context and Howler's own view of it are running, so
    // unready sounds are dropped up front rather than deferred.
    const isPlaybackReady = () => {
        if (!isVisible() || isSuspended()) {
            return false;
        }

        const ctx = howler.ctx;
        return (
            !!ctx &&
            ctx.state === 'running' &&
            (howler.state === undefined || howler.state === 'running')
        );
    };

    const markUnlocked = () => {
        if (howler.ctx) {
            unlockedContexts.add(howler.ctx);
        }
    };

    // Context-only recovery: never retains or retries the sound that
    // triggered it. Initial autoplay unlocking is left to Howler.
    const recover: AudioLifecycle['recover'] = (trigger = 'programmatic') => {
        if (!isVisible() || isSuspended()) {
            return;
        }

        const ctx = howler.ctx;
        if (
            !ctx ||
            !unlockedContexts.has(ctx) ||
            ctx.state === 'running' ||
            ctx.state === 'closed'
        ) {
            return;
        }

        // A pending programmatic resume must not block a gesture, which may be
        // what the browser needs to actually allow it.
        if (trigger === 'programmatic' && pendingResume === ctx) {
            return;
        }

        let result: Promise<void> | void;
        try {
            result = ctx.resume();
        } catch {
            return;
        }

        pendingResume = ctx;
        const settle = () => {
            if (pendingResume === ctx) {
                pendingResume = null;
            }
        };
        Promise.resolve(result).then(settle, settle);
    };

    const onGesture = () => recover('gesture');
    const onVisibilityChange = () => recover('programmatic');

    const acquire = () => {
        if (consumers++ === 0 && doc) {
            GESTURE_EVENTS.forEach((event) =>
                doc.addEventListener(event, onGesture, LISTENER_OPTIONS)
            );
            doc.addEventListener('visibilitychange', onVisibilityChange);
        }

        let released = false;
        return () => {
            if (released) {
                return;
            }
            released = true;

            if (--consumers === 0 && doc) {
                GESTURE_EVENTS.forEach((event) =>
                    doc.removeEventListener(event, onGesture, LISTENER_OPTIONS)
                );
                doc.removeEventListener('visibilitychange', onVisibilityChange);
            }
        };
    };

    const track = (howl: HowlLike) => {
        howl.on('unlock', markUnlocked);
        howl.on('play', markUnlocked);
    };

    return { isPlaybackReady, recover, track, acquire };
};
